import "../Pages.scss";
import "./PrivacyPolicy.scss";
import "../../styles/variables.scss";
import "../../styles/global.scss";

/* ========= Reusable blocks (no CSS changes) ========= */
function PageTitle({ children }) {
  return <div className="Fu-70s-70l">{children}</div>;
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section-wrapper">
      <div className="Ft-24s-24l fw600">{title}</div>
      <div className="section-text Ft-16s-24l">{children}</div>
    </div>
  );
}

function Subtitle({ children }) {
  return <div className="Ft-18s-24l fw600 subtitle">{children}</div>;
}

function Paragraph({ children }) {
  return <div className="Ft-16s-24l">{children}</div>;
}

function BulletList({ items }) {
  if (!items?.length) return null;
  return (
    <ul>
      {items.map((t, i) => (
        <li className="info-list Ft-16s-24l" key={i}>
          {t}
        </li>
      ))}
    </ul>
  );
}

/* Ordered a–d list for nested items (e.g., 4.2 in screenshot) */
function AlphaList({ items }) {
  if (!items?.length) return null;
  return (
    <ol type="a">
      {items.map((t, i) => (
        <li key={i} className="info-list Ft-16s-24l">
          {t}
        </li>
      ))}
    </ol>
  );
}

/* ================= Page ================= */
export default function PrivacyPolicy() {
  return (
    <div className="tp-pp-main-container">
      <div className="tp-pp-main-wrapper max-width">
        <PageTitle>Privacy Policy</PageTitle>

        <Section title={"1.0 \tIntroduction"}>
          <Paragraph>
            {
              'We at TorumPay ("we", "our" or "us") are committed to protecting your privacy and ensuring that your personal data is handled securely and in compliance with the Malaysian Personal Data Protection Act 2010 (PDPA), Personal Data Protection Regulations 2013 (PDPR) and other applicable data protection laws/regulations.'
            }
          </Paragraph>
          <Paragraph>
            {
              "This Privacy and Data Protection Policy (“Policy”) explains your rights, the type of personal data we collect, how we collect, use, store, disclose, and protect your personal data when you visit TorumPay and/or use our services."
            }
          </Paragraph>
          <Paragraph>
            {
              "By accessing and using TorumPay, you agree to the collection and use of your personal data in accordance with this Policy."
            }
          </Paragraph>
        </Section>

        <Section title={"1A.0 \tInterpretation"}>
          <div className="row-align">
            <Subtitle>Personal Data</Subtitle>
            <div className="right-wrapper">
              <Paragraph>
                {
                  "means any information in respect of commercial transactions, which: "
                }
              </Paragraph>
              <BulletList
                items={[
                  "is being processed wholly or partly by means of equipment operating automatically in response to instructions given for that purpose;  ",
                  "is recorded with the intention that it should wholly or partly be processed by means of such equipment; or ",
                  "is recorded as part of a relevant filing system or with the intention that it should form part of a relevant filing system,",
                  "that relates directly or indirectly to a data subject, who is identified or identifiable from that information or from that and other information in the possession of a data user, including any sensitive personal data and expression of opinion about the data subject; but does not include any information that is processed for the purpose of a credit reporting business carried on by a credit reporting agency under the Credit Reporting Agencies Act 2010.",
                ]}
              />
            </div>
          </div>

          <div className="row-align">
            <Subtitle>Sensitive Personal Data</Subtitle>
            <div className="right-wrapper">
              <Paragraph>
                {"means any personal data consisting of information as to:"}
              </Paragraph>
              <BulletList
                items={[
                  "the physical or mental health or condition of a data subject;",
                  "his political opinions;",
                  "his religious beliefs or other beliefs of a similar nature;",
                  "the commission or alleged commission by him of any offence; or",
                  "any other personal data as the Minister may determine by order published in the Gazette.",
                ]}
              />
            </div>
          </div>

          <div className="row-align">
            <Subtitle>Law</Subtitle>
            <div className="right-wrapper">
              <Paragraph>
                {
                  "means the law of Malaysia governing personal data protection including but not limited to:"
                }
              </Paragraph>
              <BulletList
                items={[
                  "Personal Data Protection Act 2010 (PDPA); ",
                  "Personal Data Protection Regulations 2013 (PDPR); and the applicable privacy laws/regulations.",
                ]}
              />
            </div>
          </div>
        </Section>

        <Section title={"2.0 \tPersonal Information We Collect"}>
          <Paragraph>
            {
              "We may collect and process the following categories of personal data:"
            }
          </Paragraph>

          <Subtitle>{"2.1\tPersonal Identification Information"}</Subtitle>
          <BulletList
            items={[
              "Full name",
              "Residential and/or mailing address",
              "Email address",
              "Phone number",
              "Date of birth",
              "National Identification (NRIC) or Passport number (wherever applicable)",
              "Other government-issued identification numbers, as required for compliance",
            ]}
          />

          <Subtitle>{"2.2\tSensitive Personal Data"}</Subtitle>
          <BulletList
            items={[
              "Biometric data, such as facial image and fingerprint",
              "Specimen signature, including electronic signatures. ",
              "Relationship with third parties, such as spouse, children or any immediate family members. (in the event of death)",
            ]}
          />

          <Subtitle>{"2.3\tFinancial and Transactional Information"}</Subtitle>
          <BulletList
            items={[
              "Bank account details which you use to transfer money to/from TorumPay",
              "Credit/debit card information (where applicable)",
              "Cryptocurrency wallet addresses",
              "Trading and transaction history",
              "Details related to deposits, withdrawals, and transfers",
            ]}
          />

          <Subtitle>{"2.4\tTechnical and Device Information"}</Subtitle>
          <BulletList
            items={[
              "Internet Protocol (IP) address used to connect your device to the internet",
              "The type of browser and version used to access TorumPay",
              "Operating system and platform",
              "Geolocation data, such as the location from which the transaction begins and ends",
              "Device identifiers (such as MAC address, device model)",
              "Cookies, web beacons SDK, log files and analytics data related to service usage",
            ]}
          />

          <Subtitle>
            {"2.5\tRegulatory and Compliance-related Information"}
          </Subtitle>
          <BulletList
            items={[
              "Know Your Customer (KYC) verification data, including identity verification documents and selfies",
              "Anti-Money Laundering (AML) checks and risk assessments",
              "Communications and correspondence related to regulatory inquiries",
            ]}
          />

          <Subtitle>{"2.6\tOther Information"}</Subtitle>
          <BulletList
            items={[
              "Your spending preferences when you use TorumPay for any third party merchant payment.",
              "Any additional information you voluntarily provide, including feedback, customer support inquiries, survey responses, or any other details not covered above.",
            ]}
          />
        </Section>

        <Section title={"2A.0\tPersonal Data Sources"}>
          <Paragraph>
            {"We gather your personal data through or from:"}
          </Paragraph>
          <BulletList
            items={[
              "TorumPay websites when you sign up for an account with us.",
              "Email, text and other electronic communications between you and TorumPay.",
              "TorumPay advertisement interactions.",
              "Data we create about you when you use our website and service, or when you attend any of our on-ground events or campaigns organised by us.",
              "Data from other sources, such as our promotional partners which you have engaged and public sources etc.",
            ]}
          />
        </Section>

        <Section title={"3.0 \tPurpose of Personal Data Collection"}>
          <Paragraph>
            {
              "We collect and process your personal data for the following purposes:"
            }
          </Paragraph>

          <Subtitle>{"3.1\tProviding and Personalizing Our Services"}</Subtitle>
          <BulletList
            items={[
              "Creating and managing your TorumPay account.",
              "Processing transactions, withdrawals, and deposits.",
              "Facilitating cryptocurrency trading and related services",
            ]}
          />

          <Subtitle>
            {"3.2\tCompliance with Legal and Regulatory Requirements"}
          </Subtitle>
          <BulletList
            items={[
              "Conducting identity verification and KYC checks.",
              "Monitoring transactions for suspicious activities under AML/CFT regulations.",
              "Reporting to regulatory authorities when required by the Law.",
            ]}
          />

          <Subtitle>{"3.3\tCommunication and Customer Support"}</Subtitle>
          <BulletList
            items={[
              "Responding to your inquiries, complaints, or feedback.",
              "Providing account-related notifications and updates.",
            ]}
          />

          <Subtitle>{"3.4\tSecurity and Fraud Prevention"}</Subtitle>
          <BulletList
            items={[
              "Detecting and preventing fraudulent activities, hacking, or security breaches.",
              "Investigating and mitigating risks associated with account misuse.",
              "Identifying, investigating, reporting, and preventing activities related to money laundering, terrorist financing, bribery, corruption, tax evasion, fraud, violations of economic or trade sanctions, or any attempts to breach laws, regulations, court orders, voluntary codes, or sanction regimes, within or outside Malaysia existing currently and in the future.",
            ]}
          />

          <Subtitle>
            {
              "3.5\tMarketing and Promotional Communications (only if you opt-in)"
            }
          </Subtitle>
          <BulletList
            items={[
              "Sending newsletters, promotional offers, and service updates.",
              "Personalizing marketing content based on your preferences.",
            ]}
          />

          <Subtitle>{"3.6\tInternal Business Operations"}</Subtitle>
          <BulletList
            items={[
              "Analyzing user trends and behavior to improve services.",
              "Conducting audits, risk assessments, and internal reporting.",
              "Managing the safety and security of our business and services.",
            ]}
          />

          <Subtitle>{"3.7\tOther Purposes"}</Subtitle>
          <Paragraph>
            {
              "Such other purposes as you may agree from time to time and being updated in this Policy."
            }
          </Paragraph>
        </Section>

        <Section title={"4.0 \tConsent and User Rights"}>
          <Paragraph>
            {
              "By using our services, you expressly consent to the collection, use, processing, and disclosure of your personal data as outlined in this Policy, as revised from time to time. If you do not agree with this Policy, you must not use our services."
            }
          </Paragraph>
          <Paragraph>
            {
              "To serve you in our best interests and safeguard your rights as our customers, you are granted with certain rights regarding the personal data which we hold about you under the Law."
            }
          </Paragraph>

          <Subtitle>{"4.1\tYour Rights"}</Subtitle>
          <BulletList
            items={[
              "You may submit a data access request in writing to us.",
              "We shall comply with such request within twenty-one (21) days from the date of receipt of such request, provided that:",
            ]}
          />
          <AlphaList
            items={[
              "your identity has been sufficiently verified by us;",
              "the requested information can be reasonably located by us within our database;",
              "the disclosure of the requested information will not cause any disclosure of another individual’s personal data;",
              "the disclosure of the requested information would not constitute a violation of court order, rules or regulations of Malaysia; ",
              "the disclosure of the requested information would not disclose confidential commercial information; and",
              "the disclosure of the requested information is not prohibited by the law of another country.",
            ]}
          />

          <Subtitle>
            {
              "4.2\tRight To Correct Inaccurate, Incomplete, Misleading Or Outdated Personal Data"
            }
          </Subtitle>
          <ul>
            <li className="info-list Ft-16s-24l">
              You may submit a data correction request in writing to us.
            </li>
            <li className="info-list Ft-16s-24l">
              We shall comply with such request within twenty-one (21) days from
              the date of receipt of such request, provided that:
              <AlphaList
                items={[
                  "your identity has been sufficiently verified by us;",
                  "the alleged inaccurate, incomplete, misleading or not up-to-date information can be reasonably corrected by us within our database;",
                  "we are satisfied that the alleged inaccurate, incomplete, misleading or not up-to-date information is in fact inaccurate, incomplete, misleading or not up-to-date; and",
                  "we are satisfied that the requested correction is inaccurate, incomplete, misleading or not up-to-date.",
                ]}
              />
            </li>
          </ul>

          <Subtitle>
            {"4.3\tRight To Withdraw Consent For Certain Processing Activities"}
          </Subtitle>
          <BulletList
            items={[
              "You may submit a consent withdrawal notice in writing to us to withdraw your consent given to us in processing your personal data. ",
              "In the event where there were already processing of personal data took place prior to the consent withdrawal notice being submitted to us, this processing shall not be deemed unlawful in any circumstances.",
            ]}
          />

          <Subtitle>
            {
              "4.4 Right To Request For The Cessation Of Direct Marketing To You"
            }
          </Subtitle>
          <BulletList
            items={[
              "You may submit a request in writing to us to cease or not to begin with the processing of your personal data for the purpose of preventing any form of direct marketing. ",
              "For the avoidance of doubt, “direct marketing” means the communication by whatever means of any advertising or marketing materials which would be directed to you should your personal data being processed by us.",
            ]}
          />

          <Subtitle>
            {"4.5\tRight To Request For Deletion Of Your Personal Data"}
          </Subtitle>
          <BulletList
            items={["You may submit a data deletion request in writing to us."]}
          />
          <Paragraph>
            {
              "To exercise any of these rights, please contact us using the details provided in Section 10."
            }
          </Paragraph>
        </Section>

        <Section title={"5.0 \tDisclosure of Personal Data"}>
          <Paragraph>
            {
              "We may disclose your personal data to the following third parties, where necessary:"
            }
          </Paragraph>

          <Subtitle>{"5.1\tAuthorised Persons"}</Subtitle>
          <BulletList
            items={[
              "Your legal representatives",
              "Anyone who can validly act on your behalf or who may be empowered to act with the requisite power and authority",
            ]}
          />

          <Subtitle>{"5.2\tService Providers"}</Subtitle>
          <BulletList
            items={[
              "Payment processors and banking institutions",
              "Cloud storage and IT service providers",
              "Customer support and communication platforms",
              "Identity verification service providers whom we engaged to verify your identity",
              "Marketing service providers",
              "Data analytic service providers",
              "Professional advisors such as legal counsels, auditors and accountants",
            ]}
          />

          <Subtitle>{"5.3\tRegulatory and Legal Authorities"}</Subtitle>
          <BulletList
            items={[
              "Securities Commission Malaysia (SC)",
              "Bank Negara Malaysia (BNM)",
              "Anti-Money Laundering service provider ",
              "Legal advisors",
              "Law enforcement agencies, government authorities, or courts as required by the law",
            ]}
          />

          <Subtitle>{"5.4\tBusiness Partners and Affiliates"}</Subtitle>
          <BulletList
            items={[
              "Our related entities ",
              "Third-party entities involved in mergers, acquisitions, or business restructuring",
              "Partners offering integrated financial services (subject to your consent)",
            ]}
          />
          <Paragraph>
            {
              "We do not sell, rent, or trade your personal information to any third parties for commercial gain."
            }
          </Paragraph>
        </Section>

        <Section title={"6.0 \tSecurity of Personal Data"}>
          <Paragraph>
            {
              "We implement strict security measures to protect your personal data from any loss, misuse, modification, unauthorized or accidental access or disclosure, alteration or destruction. These measures include:"
            }
          </Paragraph>
          <BulletList
            items={[
              "Data Encryption: Securing sensitive information using encryption protocols",
              "Access Controls: Restricting access to authorized personnel only",
              "Multi-Factor Authentication (MFA): Enhancing security for user accounts",
              "Regular Security Audits: Assessing vulnerabilities and improving cybersecurity measures",
            ]}
          />
          <Paragraph>
            {
              "However, while we strive to protect your information, no method of transmission over the internet is 100% secure. You shall be responsible for keeping your account credentials confidential."
            }
          </Paragraph>
        </Section>

        <Section title={"7.0 \tData Retention"}>
          <Paragraph>
            {
              "We retain your personal data only as long as necessary for the purposes stated in this Policy or as required by the Law."
            }
          </Paragraph>
          <Paragraph>
            {
              "Transaction records and compliance data are retained for a minimum of 7 years as required by financial regulations."
            }
          </Paragraph>
          <Paragraph>
            {
              "Inactive accounts may be anonymized or deleted after a specified period, unless required for legal reasons."
            }
          </Paragraph>
          <Paragraph>
            {
              "Upon the expiry of time relating to any business needs or legal requirements to retain your personal data, we will take reasonable steps to ensure that your personal data is deleted or anonymised. If you request the deletion of your personal data, we will assess the request in accordance with our legal and regulatory obligations."
            }
          </Paragraph>
        </Section>

        <Section title={"8.0 \tInternational Data Transfers"}>
          <Paragraph>
            {
              "While using TorumPay, your personal data may also be transferred from Malaysia to another country vice versa. By using TorumPay or your continued use of it, you have indicated your consent to these transfers."
            }
          </Paragraph>
          <Paragraph>
            {
              "If we transfer your personal data outside of Malaysia, we will ensure that the receiving party provides at least the same level of protection required under the Law."
            }
          </Paragraph>
        </Section>

        <Section title={"9.0 \tChanges to this Policy"}>
          <Paragraph>
            {
              'We may update this Policy from time to time. Any changes will be posted on TorumPay with an updated "Last Revised/Amended" date.'
            }
          </Paragraph>
          <Paragraph>
            {
              "If the changes are significant, we will notify users via email or other communication channels. Your continued use of TorumPay constitutes an explicit acceptance of any updates."
            }
          </Paragraph>
        </Section>

        <Section title={"10.0 \tContact Us"}>
          <Paragraph>
            {
              "If you have any questions or requests regarding the processing of your Personal Data, you may contact us at:"
            }
          </Paragraph>
          <div className="info-list Ft-16s-24l">
            Email: <span className="fw600">support@torum.com</span>
          </div>
        </Section>
      </div>
    </div>
  );
}
